import {
  INSIGHT_CATEGORY_ATTR,
  INSIGHT_ID_ATTR,
  INSIGHT_TITLE_ATTR,
} from '@design/chat/mention-composer';
import { config } from '@terminal/config';

import { PDF_FONT_FAMILY } from '../../alfred/research/utils/htmlNodes';

import { InsightShareCategory } from './buildInsightShareUrl';
import { prepareClipboardHtml } from './prepareClipboardHtml';
import { withExpandedDrawerAccordions } from './prepareDrawerContent';
import { rasterizeSrcToPng } from './rasterizeClipboardImage';

const BRAND_NAME_PATH = '/app/static/images/brand/brand-name-dark.svg';
const HEADER_LOGO_WIDTH_PX = 103;
const HEADER_LOGO_MARGIN_BOTTOM_PX = 24;
const INSIGHT_LINK_MARGIN_TOP_PX = 24;

interface HandleCopyDrawerArgs {
  contentToCopy: HTMLDivElement;
  expandAccordions?: (expand: boolean) => void;
  insightId?: string;
  insightUrl?: string;
  insightLinkLabel?: string;
  insightCategory?: InsightShareCategory;
}

const buildBrandHeaderHtml = async (): Promise<string> => {
  const png = await rasterizeSrcToPng({
    src: `${config.urls.root}${BRAND_NAME_PATH}`,
    width: HEADER_LOGO_WIDTH_PX,
  });
  if (!png) {
    return '';
  }

  return `<div style="margin-bottom:${HEADER_LOGO_MARGIN_BOTTOM_PX}px;"><img src="${png}" alt="Company logo" width="${HEADER_LOGO_WIDTH_PX}" style="width:${HEADER_LOGO_WIDTH_PX}px;height:auto;" /></div>`;
};

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/**
 * Also carries `data-insight-id`/`data-insight-title`/`data-insight-category`
 * so `MentionComposerInput` can recognize a pasted insight, collapse it into
 * a chip instead of the full drawer HTML, and pick the chip's icon/color to
 * match the insight's own category badge.
 */
const buildInsightLinkHtml = ({
  url,
  label,
  insightId,
  insightCategory,
}: {
  url: string;
  label: string;
  insightId?: string;
  insightCategory?: InsightShareCategory;
}): string =>
  `<div style="margin-top:${INSIGHT_LINK_MARGIN_TOP_PX}px;"><a href="${url}"${
    insightId
      ? ` ${INSIGHT_ID_ATTR}="${escapeHtml(insightId)}" ${INSIGHT_TITLE_ATTR}="${escapeHtml(label)}"${
          insightCategory
            ? ` ${INSIGHT_CATEGORY_ATTR}="${escapeHtml(insightCategory)}"`
            : ''
        }`
      : ''
  }>${escapeHtml(label)}</a></div>`;

const writeClipboard = ({
  html,
  plainContent,
  toPng,
}: {
  html: string;
  plainContent: string;
  toPng: () => Promise<string | undefined>;
}) => {
  const clipboardDocument = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8">
      </head>
      <body style="font-family:${PDF_FONT_FAMILY};background:#ffffff;">
        ${html}
      </body>
    </html>
  `;

  return navigator.clipboard
    .write([
      new ClipboardItem({
        'text/plain': new Blob([plainContent.trim()], { type: 'text/plain' }),
        'text/html': new Blob([clipboardDocument], { type: 'text/html' }),
      }),
    ])
    .catch(() =>
      toPng()
        .then(pngDataUrl => {
          if (!pngDataUrl) {
            throw new Error('Clipboard write failed');
          }
          return fetch(pngDataUrl);
        })
        .then(response => response.blob())
        .then(pngBlob =>
          navigator.clipboard.write([
            new ClipboardItem({ 'image/png': pngBlob }),
          ])
        )
    );
};

export const handleCopyDrawer = ({
  contentToCopy,
  expandAccordions,
  insightId,
  insightUrl,
  insightLinkLabel,
  insightCategory,
}: HandleCopyDrawerArgs): Promise<void> => {
  let disposePrepared: (() => void) | undefined;

  return withExpandedDrawerAccordions({
    expandAccordions,
    run: () => {
      const insightLinkHtml =
        insightUrl && insightLinkLabel
          ? buildInsightLinkHtml({
              url: insightUrl,
              label: insightLinkLabel,
              insightId,
              insightCategory,
            })
          : '';
      const plainContent = insightUrl
        ? `${contentToCopy.innerText}\n\n${insightLinkLabel}: ${insightUrl}`
        : contentToCopy.innerText;

      return buildBrandHeaderHtml()
        .then(brandHeaderHtml =>
          prepareClipboardHtml(contentToCopy, brandHeaderHtml)
        )
        .then(prepared => {
          disposePrepared = prepared.dispose;
          return writeClipboard({
            html: `${prepared.html}${insightLinkHtml}`,
            plainContent,
            toPng: prepared.toPng,
          });
        });
    },
  }).finally(() => {
    disposePrepared?.();
  });
};
